// The wall itself. Rendered in a Shadow DOM so the shop's styles and ours can't clash.
// All text from posts goes in with textContent, never as HTML.
import type { PublicProduct, PublicWall, PublicWallPost } from "@brandwall/contracts";
import type { WallWords } from "@brandwall/contracts/words";

/** Fills in {name}. */
const fill = (text: string, name: string) => text.replace("{name}", name);

export type WallEvent = "view" | "open" | "click" | "add";

/** A visitor's report about a post (D-096). Resolves true once the brand has been told. */
export type Report = (
  postId: string,
  r: { reason: string; message?: string; email?: string },
) => Promise<boolean>;

const REPORT_REASONS: [string, keyof WallWords][] = [
  ["privacy", "reasonPrivacy"],
  ["offensive", "reasonOffensive"],
  ["spam", "reasonSpam"],
  ["other", "reasonOther"],
];

/** "Report" in an opened post: a short form under the links, and a thank-you once it's sent. */
function reportLink(postId: string, report: Report, links: HTMLElement, w: WallWords) {
  const flag = h("span", { "aria-hidden": "true" });
  flag.innerHTML =
    '<svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" stroke-width="2"><path d="M5 21V4m0 0h11l-2 4 2 4H5"/></svg>';
  const btn = h("button", { type: "button", class: "report" }, flag, w.report);
  btn.addEventListener("click", () => {
    if (links.nextElementSibling?.matches("form.rep")) return;
    const form = h("form", { class: "rep", "aria-label": w.reportPost });
    const legend = h("b", {}, w.whatsWrong);
    form.append(legend);
    for (const [value, label] of REPORT_REASONS) {
      const input = h("input", { type: "radio", name: "reason", value, required: "" });
      form.append(h("label", {}, input, w[label]));
    }
    const message = h("textarea", { name: "message", rows: "2", maxlength: "2000" });
    const email = h("input", { type: "email", name: "email", maxlength: "254" });
    form.append(
      h("label", { style: "flex-direction:column" }, w.anythingElse, message),
      h("label", { style: "flex-direction:column" }, w.yourEmail, email),
    );
    const send = h("button", { type: "submit" }, w.sendReport);
    const cancel = h("button", { type: "button" }, w.cancel);
    cancel.addEventListener("click", () => form.remove());
    form.append(h("div", { class: "row2" }, send, cancel));
    const status = h("p", { role: "status", style: "margin:0" });
    form.append(status);
    form.addEventListener("submit", async (e) => {
      e.preventDefault();
      const reason = (form.querySelector("input[name=reason]:checked") as HTMLInputElement | null)
        ?.value;
      if (!reason) return;
      send.setAttribute("disabled", "");
      const ok = await report(postId, {
        reason,
        message: message.value.trim() || undefined,
        email: email.value.trim() || undefined,
      }).catch(() => false);
      if (ok) form.replaceChildren(h("p", { role: "status", style: "margin:0" }, w.thanks));
      else {
        status.textContent = w.notSent;
        send.removeAttribute("disabled");
      }
    });
    links.after(form);
    (form.querySelector("input") as HTMLInputElement | null)?.focus();
  });
  return btn;
}
type Track = (type: WallEvent, postId?: string, product?: PublicProduct) => void;

// The brand's font first, then whatever font the page around the wall uses.
const fontStack = (font: string, fallback: string) => {
  const clean = fallback.replace(/[{};<>]/g, "") || "system-ui, sans-serif";
  return font === "inherit" ? clean : `"${font.replace(/["\\{};<>]/g, "")}", ${clean}`;
};

const css = (d: PublicWall["design"], fallbackFont: string) => `
:host{all:initial;display:block;contain:layout style;font-family:${fontStack(d.font, fallbackFont)};color:${d.text}}
*{box-sizing:border-box}
.bw{background:${d.background};padding:${d.spacing}px 0}
h2{font:inherit;font-size:clamp(20px,2.4vw,28px);font-weight:700;margin:0;letter-spacing:-.01em;color:${d.text}}
.words{display:grid;gap:8px;justify-items:start;margin:0 0 14px}
.words.below{margin:14px 0 0}
.words.center{text-align:center;justify-items:center}
.words p{margin:0;max-width:60ch;line-height:1.5}
.words .tag a{color:inherit;font-weight:600}
.labels{display:flex;flex-wrap:wrap;gap:8px;margin:0 0 12px}
.labels button{font:inherit;font-size:14px;padding:6px 14px;border-radius:999px;border:1px solid ${d.accent};background:transparent;color:${d.text};cursor:pointer}
.labels button[aria-pressed=true]{background:${d.accent};color:#fff}
.words .cta{display:inline-block;margin-top:4px;padding:10px 16px;border-radius:${Math.min(d.corners, 999)}px;background:${d.accent};color:#fff;font-weight:600;text-decoration:none}
.grid{display:grid;grid-template-columns:repeat(${d.columns},1fr);gap:${d.spacing}px;grid-auto-flow:dense}
.sized .tile{aspect-ratio:auto;height:100%}
.sized .s2{grid-column:span 2;grid-row:span 2}
.sized .s3{grid-column:span 3;grid-row:span 3}
.masonry{columns:${d.columns};column-gap:${d.spacing}px}
.masonry .tile{margin-bottom:${d.spacing}px;break-inside:avoid}
.row{display:grid;grid-auto-flow:column;grid-auto-columns:calc((100% - ${d.spacing * 3}px)/4);gap:${d.spacing}px;overflow-x:auto;scroll-snap-type:x mandatory;scrollbar-width:none}
.row::-webkit-scrollbar{display:none}
.row .tile{scroll-snap-align:start}
.is-strip .row{grid-auto-columns:calc((100% - ${d.spacing * 5}px)/6)}
.tile{position:relative;display:block;width:100%;padding:0;border:0;background:#eee;border-radius:${d.corners}px;overflow:hidden;cursor:pointer;aspect-ratio:4/5}
.masonry .tile{aspect-ratio:auto}
.tile img{display:block;width:100%;height:100%;object-fit:cover;transition:transform .3s}
.masonry .tile img{height:auto}
.tile:hover img,.tile:hover video{transform:scale(1.03)}
.tile:focus-visible{outline:3px solid ${d.accent};outline-offset:2px}
.tile video{position:absolute;inset:0;width:100%;height:100%;object-fit:cover;opacity:0;transition:opacity .3s,transform .3s;pointer-events:none}
.tile video.on{opacity:1}
.play{position:absolute;right:8px;top:8px;width:26px;height:26px;border-radius:50%;background:rgba(0,0,0,.55);display:grid;place-items:center}
.play::after{content:"";border-left:9px solid #fff;border-top:6px solid transparent;border-bottom:6px solid transparent;margin-left:2px}
.nav{display:flex;gap:8px;justify-content:flex-end;margin-top:10px}
button.arrow{width:38px;height:38px;border-radius:50%;border:1px solid rgba(0,0,0,.15);background:#fff;color:${d.text};font-size:18px;cursor:pointer}
button.arrow:focus-visible,dialog button:focus-visible,dialog a:focus-visible{outline:3px solid ${d.accent};outline-offset:2px}
.credit{display:block;margin-top:10px;font-size:12px;color:${d.text};opacity:.6;text-align:right;text-decoration:none}
dialog{border:0;padding:0;border-radius:14px;max-width:min(980px,94vw);width:100%;background:#fff;color:#10302a}
dialog::backdrop{background:rgba(10,20,18,.72)}
.lb{display:grid;grid-template-columns:minmax(0,1.3fr) minmax(0,1fr)}
.lb .media{position:relative;background:#000;display:grid;place-items:center;min-height:300px}
.nosound{position:absolute;left:10px;top:10px;width:30px;height:30px;border-radius:50%;background:rgba(0,0,0,.6);display:grid;place-items:center}
.nosound svg{width:18px;height:18px}
.lb .media img,.lb .media video{display:block;max-width:100%;max-height:82vh;object-fit:contain}
.lb .media iframe.ig{display:block;width:min(400px,100%);height:min(720px,82vh);border:0;background:#fff;margin:0 auto}
.lb .side{padding:22px 22px 18px;display:flex;flex-direction:column;gap:12px;overflow:auto;max-height:82vh}
.lb .who{font-weight:700}
.lb .cap{white-space:pre-wrap;line-height:1.45;font-size:14px}
.lb a.ig{color:inherit;font-size:13px}
.links{display:flex;gap:14px;align-items:center;flex-wrap:wrap}
button.report{border:0;background:none;padding:0;color:inherit;opacity:.8;font:inherit;font-size:13px;text-decoration:underline;cursor:pointer;display:inline-flex;align-items:center;gap:4px}
button.report span{display:flex}
form.rep{display:flex;flex-direction:column;gap:8px;border-top:1px solid #e4e4db;padding-top:10px;font-size:13px}
form.rep label{display:flex;gap:6px;align-items:flex-start}
form.rep textarea,form.rep input[type=email]{font:inherit;font-size:13px;border:1px solid #d5d7cd;border-radius:8px;padding:6px 8px;width:100%}
form.rep .row2{display:flex;gap:8px}
form.rep button{border:1px solid #d5d7cd;background:#fff;border-radius:9px;padding:6px 10px;font:inherit;font-size:13px;cursor:pointer}
form.rep button[type=submit]{background:${d.accent};border-color:${d.accent};color:#fff}
.lb .bar{display:flex;gap:8px;margin-top:auto}
.lb .bar button{border:1px solid #d5d7cd;background:#fff;border-radius:9px;padding:8px 12px;font:inherit;font-size:14px;cursor:pointer}
.close{position:absolute;right:10px;top:10px;width:36px;height:36px;border-radius:50%;border:0;background:rgba(255,255,255,.9);font-size:20px;cursor:pointer}
.bag{position:absolute;left:8px;bottom:8px;width:26px;height:26px;border-radius:50%;background:rgba(255,255,255,.92);display:grid;place-items:center}
.bag svg{width:14px;height:14px}
.frame{position:relative;display:inline-block;line-height:0;max-width:100%}
.dot{position:absolute;transform:translate(-50%,-50%);width:28px;height:28px;border-radius:50%;border:2px solid #fff;background:${d.accent};color:#fff;font:700 13px/24px system-ui,sans-serif;text-align:center;cursor:pointer;box-shadow:0 2px 8px rgba(0,0,0,.35);padding:0}
.shop{border-top:1px solid #e4e4db;padding-top:12px;display:flex;flex-direction:column;gap:10px}
.shop h3{margin:0;font-size:15px}
.item{display:grid;grid-template-columns:22px 48px 1fr auto;gap:10px;align-items:center}
.item .n{width:22px;height:22px;border-radius:50%;background:${d.accent};color:#fff;font:700 12px/22px system-ui,sans-serif;text-align:center}
.item img{width:48px;height:48px;object-fit:cover;border-radius:8px;background:#f1f1eb}
.item b{display:block;font-size:14px}
.item span{font-size:13px;opacity:.75}
.item a,.item button{border:0;background:${d.accent};color:#fff;border-radius:9px;padding:8px 10px;font:inherit;font-size:13px;font-weight:700;cursor:pointer;text-decoration:none;white-space:nowrap}
.item.hl{outline:2px solid ${d.accent};outline-offset:4px;border-radius:8px}
@media (max-width:700px){
  .grid{grid-template-columns:repeat(2,1fr)}
  .sized .s3{grid-column:span 2;grid-row:span 2}
  .masonry{columns:2}
  .row{grid-auto-columns:70%}
  .is-strip .row{grid-auto-columns:42%}
  dialog{max-width:100vw;width:100vw;height:100dvh;max-height:100dvh;border-radius:0;margin:0}
  .lb{grid-template-columns:1fr}
  .lb .media img,.lb .media video{max-height:60vh;width:100%}
  .lb .media .frame{width:100%}
}
@media (prefers-reduced-motion:reduce){.tile img{transition:none}}
`;

/** Instagram's own player for a post link (D-082). The same as instagramEmbedUrl in contracts. */
function embedUrl(permalink: string | null | undefined): string | null {
  const m = /^https:\/\/(?:www\.)?instagram\.com\/(?:[\w.]+\/)?(?:p|reel|reels|tv)\/([\w-]+)/.exec(
    permalink ?? "",
  );
  return m ? `https://www.instagram.com/p/${m[1]}/embed/` : null;
}

function safeUrl(u: string | null | undefined): string | null {
  if (!u) return null;
  try {
    const url = new URL(u);
    return url.protocol === "https:" || url.protocol === "http:" ? url.toString() : null;
  } catch {
    return null;
  }
}

function h<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  attrs: Record<string, string | null | undefined> = {},
  ...children: (Node | string | null)[]
): HTMLElementTagNameMap[K] {
  const el = document.createElement(tag);
  for (const [k, v] of Object.entries(attrs)) if (v != null) el.setAttribute(k, v);
  for (const c of children) if (c != null) el.append(c);
  return el;
}

/**
 * Video tiles that move (D-094, D-101): a tile plays, silent and on a loop, only while the mouse
 * is on it, and pauses when the mouse leaves. A tap on a phone opens the post instead. Paused
 * while a post is open. Visitors who ask for less motion or to save data get still pictures.
 * Null when tiles stay still.
 */
interface TilePlayer {
  /** The tile's video for this file, reused across renders so editing never downloads it again. */
  video(url: string): HTMLVideoElement;
  watch(video: HTMLVideoElement, tile: HTMLElement): void;
  /** Pauses every tile while a post is open, and lets them play again after. */
  hold(on: boolean): void;
}
type PlayerState = {
  cache: Map<string, HTMLVideoElement>;
  update: () => void;
};
const players = new WeakMap<ShadowRoot | HTMLElement, PlayerState>();
const media = (q: string) => typeof matchMedia === "function" && matchMedia(q).matches;
const keepStill = () =>
  media("(prefers-reduced-motion: reduce)") ||
  Boolean((navigator as { connection?: { saveData?: boolean } }).connection?.saveData);

function tilePlayer(root: ShadowRoot | HTMLElement, on: boolean): TilePlayer | null {
  const prev = players.get(root);
  if (!on || keepStill()) {
    for (const v of prev?.cache.values() ?? []) v.pause();
    players.delete(root);
    return null;
  }
  const cache = prev?.cache ?? new Map<string, HTMLVideoElement>();
  let held = false;
  let pointed: HTMLVideoElement | null = null;
  const update = () => {
    for (const v of cache.values()) if (v !== pointed || held || keepStill()) v.pause();
    if (pointed && !held && !keepStill()) {
      if (!pointed.getAttribute("src") && pointed.dataset.src) pointed.src = pointed.dataset.src;
      pointed.play().catch(() => {});
    }
  };
  const point = (v: HTMLVideoElement | null) => {
    pointed = v;
    update();
  };
  if (!prev && typeof matchMedia === "function") {
    // Asking for less motion after the page has loaded stops the tiles at once.
    matchMedia("(prefers-reduced-motion: reduce)").addEventListener?.("change", () =>
      players.get(root)?.update(),
    );
  }
  players.set(root, { cache, update });
  // Drawn again (the editor, a label tapped): nothing plays until the mouse is on a tile.
  update();
  return {
    video(url) {
      let v = cache.get(url);
      if (!v) {
        v = h("video", {
          muted: "",
          loop: "",
          playsinline: "",
          preload: "none",
          "aria-hidden": "true",
          tabindex: "-1",
          "data-src": url,
        });
        const el = v;
        // Tiles are always silent, whatever the wall's sound: sound only plays when a post is
        // opened.
        el.muted = true;
        el.addEventListener("volumechange", () => {
          if (!el.muted) el.muted = true;
        });
        el.addEventListener("playing", () => el.classList.add("on"));
        cache.set(url, el);
      }
      return v;
    },
    watch(v, t) {
      // A finger has no hover: a tap opens the post, where the video plays.
      t.addEventListener("pointerenter", (e) => e.pointerType !== "touch" && point(v));
      t.addEventListener("pointerleave", () => pointed === v && point(null));
    },
    hold(on) {
      held = on;
      update();
    },
  };
}

function tile(
  p: PublicWallPost,
  onOpen: () => void,
  masonry: boolean,
  player: TilePlayer | null,
  w: WallWords,
) {
  const img = h("img", {
    src: safeUrl(p.src),
    srcset: p.srcset,
    sizes: "(max-width:700px) 50vw, 25vw",
    alt: p.alt,
    loading: "lazy",
    decoding: "async",
    width: p.width ? String(p.width) : null,
    height: p.height ? String(p.height) : null,
  });
  const btn = h(
    "button",
    { class: "tile", type: "button", "aria-label": fill(w.openPost, `@${p.creator}`) },
    img,
  );
  if (masonry && p.width && p.height) btn.style.aspectRatio = `${p.width}/${p.height}`;
  // Only videos Brandwall has its own copy of move, from their light tile loop when there is one;
  // the rest play in Instagram's player when opened, and keep their still picture here.
  const clip = player && p.type === "video" ? safeUrl(p.tile ?? p.video) : null;
  if (player && clip) {
    const v = player.video(clip);
    btn.append(v);
    player.watch(v, btn);
  }
  if (p.type === "video") btn.append(h("span", { class: "play", "aria-hidden": "true" }));
  if (p.products?.length) {
    const bag = h("span", { class: "bag", "aria-hidden": "true" });
    bag.innerHTML =
      '<svg viewBox="0 0 24 24" fill="none" stroke="#10302a" stroke-width="2"><path d="M6 8h12l-1 12H7z"/><path d="M9 8a3 3 0 0 1 6 0"/></svg>';
    btn.append(bag);
    btn.setAttribute("aria-label", fill(w.openPostShop, `@${p.creator}`));
  }
  btn.addEventListener("click", onOpen);
  return btn;
}

/** "Add to bag" works when the wall runs on the brand's own Shopify shop. */
function canAddToBag(shop: PublicWall["shop"]) {
  if (shop?.kind !== "shopify") return false;
  const w = window as unknown as { Shopify?: { shop?: string } };
  return location.origin === shop.origin || Boolean(w.Shopify?.shop);
}

function shopList(
  p: PublicWallPost,
  shop: PublicWall["shop"],
  track: Track,
  w: WallWords,
): { el: HTMLElement; highlight: (n: number) => void } | null {
  const products = p.products ?? [];
  if (products.length === 0) return null;
  const items = products.map((pr: PublicProduct) => {
    const url = safeUrl(pr.url);
    let action: HTMLElement | null = null;
    if (canAddToBag(shop) && pr.variantId) {
      const b = h("button", { type: "button" }, w.addToBag);
      b.addEventListener("click", async () => {
        b.textContent = w.adding;
        try {
          const r = await fetch("/cart/add.js", {
            method: "POST",
            headers: { "content-type": "application/json" },
            body: JSON.stringify({ items: [{ id: Number(pr.variantId), quantity: 1 }] }),
          });
          b.textContent = r.ok ? w.added : w.tryAgain;
          if (r.ok) track("add", p.id, pr);
        } catch {
          b.textContent = w.tryAgain;
        }
      });
      action = b;
    } else if (url) {
      const a = h("a", { href: url }, w.view);
      a.addEventListener("click", () => track("click", p.id, pr));
      action = a;
    }
    return h(
      "div",
      { class: "item", "data-n": String(pr.n) },
      h("span", { class: "n", "aria-hidden": "true" }, String(pr.n)),
      pr.image ? h("img", { src: safeUrl(pr.image), alt: "", loading: "lazy" }) : h("span"),
      h("div", {}, h("b", {}, pr.title), pr.price ? h("span", {}, pr.price) : null),
      action,
    );
  });
  const el = h("div", { class: "shop" }, h("h3", {}, w.shopThisLook), ...items);
  return {
    el,
    highlight(n: number) {
      for (const i of items) i.classList.toggle("hl", i.dataset.n === String(n));
      items[n - 1]?.querySelector<HTMLElement>("a,button")?.focus();
    },
  };
}

function lightbox(
  root: ShadowRoot,
  posts: PublicWallPost[],
  shop: PublicWall["shop"],
  track: Track,
  /** Told when a post opens (true) and closes (false). */
  onShow: (open: boolean) => void = () => {},
  /** Sends a visitor's report; without it (the wall editor) there's no Report link. */
  report: Report | undefined,
  w: WallWords,
  lang: string,
) {
  const dialog = h("dialog", { "aria-label": w.post, lang });
  root.append(dialog);
  let index = 0;
  let opener: HTMLElement | null = null;

  const render = () => {
    const p = posts[index]!;
    track("open", p.id);
    const media = h("div", { class: "media" });
    const shopping = shopList(p, shop, track, w);
    if (p.type === "video" && safeUrl(p.video)) {
      const video = h("video", {
        src: safeUrl(p.video),
        poster: safeUrl(p.src),
        controls: "",
        playsinline: "",
        preload: "metadata",
        muted: p.muted ? "" : null,
      });
      media.append(video);
      if (p.muted) {
        // The brand chose no sound for this video (D-092): it stays muted, whatever the
        // player's own volume button does.
        video.muted = true;
        video.addEventListener("volumechange", () => {
          if (!video.muted) video.muted = true;
        });
        const icon = h("span", { class: "nosound", role: "img", "aria-label": w.noSound });
        icon.innerHTML =
          '<svg viewBox="0 0 24 24" fill="none" stroke="#fff" stroke-width="2" aria-hidden="true"><path d="M4 9h4l5-4v14l-5-4H4z"/><path d="m17 9 5 6m0-6-5 6"/></svg>';
        media.append(icon);
      }
    } else if (p.type === "video" && embedUrl(p.permalink)) {
      // No copy of the video (demo walls, or Instagram never gave the file): Instagram's own
      // player (D-082, D-090).
      media.append(
        h("iframe", {
          class: "ig",
          src: embedUrl(p.permalink)!,
          title: fill(w.videoBy, `@${p.creator}`),
          allow: "autoplay; encrypted-media; picture-in-picture",
          loading: "lazy",
        }),
      );
    } else {
      const frame = h(
        "div",
        { class: "frame" },
        h("img", { src: safeUrl(p.src), srcset: p.srcset, sizes: "60vw", alt: p.alt }),
      );
      // Dots sit at the same spot of the photo on every screen: positions are fractions of it.
      for (const pr of p.products ?? []) {
        const dot = h(
          "button",
          {
            class: "dot",
            type: "button",
            style: `left:${(pr.x * 100).toFixed(2)}%;top:${(pr.y * 100).toFixed(2)}%`,
            "aria-label": `${pr.title}${pr.price ? `, ${pr.price}` : ""}`,
          },
          String(pr.n),
        );
        dot.addEventListener("click", () => shopping?.highlight(pr.n));
        frame.append(dot);
      }
      media.append(frame);
    }
    const who = h("div", { class: "who" }, `@${p.creator}`);
    const cap = h("div", { class: "cap", lang: "" });
    cap.textContent = p.caption ?? "";
    const link = safeUrl(p.permalink);
    const prev = h("button", { type: "button", "aria-label": w.previousPost }, `‹ ${w.previous}`);
    const next = h("button", { type: "button", "aria-label": w.nextPost }, `${w.next} ›`);
    prev.addEventListener("click", () => go(-1));
    next.addEventListener("click", () => go(1));
    const close = h("button", { class: "close", type: "button", "aria-label": w.close }, "×");
    close.addEventListener("click", () => dialog.close());
    dialog.replaceChildren(
      h(
        "div",
        { class: "lb" },
        media,
        h(
          "div",
          { class: "side" },
          who,
          cap,
          (() => {
            const links = h("div", { class: "links" });
            if (link)
              links.append(
                h(
                  "a",
                  { class: "ig", href: link, target: "_blank", rel: "noopener" },
                  w.viewOnInstagram,
                ),
              );
            if (report) links.append(reportLink(p.id, report, links, w));
            return links.childNodes.length ? links : null;
          })(),
          shopping?.el ?? null,
          posts.length > 1 ? h("div", { class: "bar" }, prev, next) : null,
        ),
      ),
      close,
    );
    close.focus();
  };
  const go = (d: number) => {
    index = (index + d + posts.length) % posts.length;
    render();
  };
  dialog.addEventListener("keydown", (e) => {
    if (e.key === "ArrowRight") go(1);
    if (e.key === "ArrowLeft") go(-1);
  });
  dialog.addEventListener("close", () => {
    onShow(false);
    dialog.querySelector("video")?.pause();
    // An Instagram player keeps playing unless it's taken away.
    dialog.querySelector("iframe.ig")?.remove();
    opener?.focus();
  });
  dialog.addEventListener("click", (e) => {
    if (e.target === dialog) dialog.close();
  });
  return (i: number, from: HTMLElement) => {
    index = i;
    opener = from;
    onShow(true);
    render();
    dialog.showModal();
  };
}

/** The website's own heading and text style, sampled from the page around the wall (D-098). */
export interface SiteStyle {
  heading: string | null;
  text: string | null;
}

const STYLE_PROPS = [
  "font-family",
  "font-size",
  "font-weight",
  "font-style",
  "line-height",
  "letter-spacing",
  "text-transform",
  "color",
] as const;

const rgb = (c: string) => {
  const hex = c.match(/^#([0-9a-f]{6})$/i)?.[1];
  const n = hex
    ? [0, 2, 4].map((k) => Number.parseInt(hex.slice(k, k + 2), 16))
    : (c.match(/[\d.]+/g) ?? []).map(Number);
  return n.length >= 3 ? [n[0]!, n[1]!, n[2]!, n[3] ?? 1] : null;
};
/** Rough lightness from 0 to 1: enough to tell text that would vanish on the background. */
const lum = (c: number[]) => (0.2126 * c[0]! + 0.7152 * c[1]! + 0.0722 * c[2]!) / 255;

/**
 * The style of the page's own heading and paragraph nearest the wall: visible, in the page's
 * content (not a header, banner, menu or pop-up). Its colour only when it reads well on the
 * wall's background, and never bigger than a wall's headline needs.
 */
export function siteStyle(host: HTMLElement, background: string): SiteStyle {
  const bg = rgb(background);
  const sample = (selector: string) => {
    let best: HTMLElement | null = null;
    for (const el of Array.from(document.querySelectorAll<HTMLElement>(selector))) {
      if (host.contains(el) || el.contains(host) || !el.textContent?.trim()) continue;
      if (el.closest("header,footer,nav,aside,dialog,[aria-hidden=true],[role=dialog]")) continue;
      const cs = getComputedStyle(el);
      const r = el.getBoundingClientRect();
      if (r.width < 8 || r.height < 8 || cs.visibility === "hidden" || Number(cs.opacity) === 0)
        continue;
      if (/fixed|sticky|absolute/.test(cs.position)) continue;
      // The last one before the wall, else the first after it.
      if (host.compareDocumentPosition(el) & Node.DOCUMENT_POSITION_PRECEDING) best = el;
      else {
        best ??= el;
        break;
      }
    }
    if (!best) return null;
    const cs = getComputedStyle(best);
    const out = STYLE_PROPS.filter((p) => p !== "color" && p !== "font-size").map(
      (p) => `${p}:${cs.getPropertyValue(p)}`,
    );
    out.push(`font-size:min(${cs.fontSize},44px)`);
    const c = rgb(cs.color);
    if (c && bg && c[3]! > 0.5) {
      if (Math.abs(lum(c) - lum(bg)) > 0.4) out.push(`color:${cs.color}`);
    }
    return out.join(";");
  };
  return { heading: sample("h2") ?? sample("h1"), text: sample("p") };
}

/** "Share your moments with @brand or #tag", with the account and hashtag as links. */
function tagLine(tag: { account: string | null; hashtag: string | null }, w: WallWords) {
  const line = h("p", { class: "tag" });
  const template = tag.account ? (tag.hashtag ? w.tagUsOr : w.tagUs) : w.tagHashtag;
  for (const part of template.split(/(\{account\}|\{hashtag\})/)) {
    const value = part === "{account}" ? tag.account : part === "{hashtag}" ? tag.hashtag : null;
    if (!value) {
      if (part && !part.startsWith("{")) line.append(part);
      continue;
    }
    const name = value.slice(1);
    const href = value.startsWith("#")
      ? `https://www.instagram.com/explore/tags/${encodeURIComponent(name)}/`
      : `https://www.instagram.com/${encodeURIComponent(name)}/`;
    line.append(h("a", { href, target: "_blank", rel: "noopener" }, value));
  }
  return line;
}

export function renderWall(
  root: ShadowRoot,
  wall: PublicWall,
  fallbackFont = "system-ui, sans-serif",
  track: Track = () => {},
  /** Fetches the next posts for walls that keep loading as visitors scroll. */
  loadMore?: (offset: number) => Promise<PublicWall | null>,
  /** The wall editor: a click on a post edits it instead of opening it. */
  onEdit?: (post: PublicWallPost, tile: HTMLElement) => void,
  /** Sends a visitor's report about a post (D-096); the editor has none. */
  report?: Report,
  /** The website's own heading and text style; the editor has none and uses the wall's. */
  site?: SiteStyle,
  /** Shows the posts with one label, or all (null), when a visitor taps a label (D-100). */
  pick?: (label: string | null) => void,
) {
  const d = wall.design;
  // The shopper's language (D-097): the server sends every word; one it didn't is left empty.
  const w = new Proxy((wall.words ?? {}) as Record<string, string>, {
    get: (o, k) => o[k as string] ?? "",
  }) as unknown as WallWords;
  const lang = wall.lang ?? "en";
  const style = h("style");
  style.textContent = css(d, fallbackFont);
  const stacked = wall.layout === "grid" || wall.layout === "masonry";
  const infinite = stacked && d.infinite;
  // Infinite walls start with two rows and add rows while the end of the wall is near the screen.
  const batch = d.columns * 2;
  const first = infinite
    ? batch
    : wall.layout === "grid"
      ? d.rows * d.columns
      : wall.layout === "masonry"
        ? d.rows * d.columns * 2
        : 24;
  const all = [...wall.posts];
  const grid = wall.layout === "grid";
  // A big post takes the room of several: 2x2 is four cells, 3x3 nine (never wider than the wall).
  const size = (p: PublicWallPost) => (grid ? Math.min(p.size ?? 1, d.columns) : 1);
  let shown: PublicWallPost[];
  if (grid && !infinite) {
    shown = [];
    let used = 0;
    for (const p of all) {
      const cells = size(p) ** 2;
      if (used + cells > first) continue;
      shown.push(p);
      used += cells;
      if (used >= first) break;
    }
  } else shown = all.slice(0, first);
  let next = wall.next;
  const player = tilePlayer(root, (wall.tiles ?? "play") === "play");
  // Drawn again (a label tapped): the last drawing's post viewer goes.
  for (const d of root.querySelectorAll("dialog")) d.remove();
  const open = lightbox(
    root,
    shown,
    wall.shop ?? null,
    track,
    (o) => player?.hold(o),
    report,
    w,
    lang,
  );
  const makeTile = (p: PublicWallPost, i: number) => {
    const t: HTMLElement = tile(
      p,
      () => (onEdit ? onEdit(p, t) : open(i, t)),
      wall.layout === "masonry",
      player,
      w,
    );
    const s = size(p);
    return h(
      "div",
      { role: "listitem", class: s > 1 ? `s${s}` : null, "data-id": onEdit ? p.id : null },
      t,
    );
  };
  const tiles = shown.map(makeTile);
  const section = h("section", {
    class: `bw is-${wall.layout}`,
    "aria-label": w.posts,
    lang,
  });
  // The wall's own words around its posts (D-098); older answers only have the heading. They
  // live inside the wall, so they hide with it.
  const t = wall.text ?? null;
  const heading = t ? t.heading : d.heading || null;
  const own = t?.style === "site" && site ? site : null;
  const words = (where: "above" | "below") => {
    const box = h("div", { class: `words ${where}${t?.align === "center" ? " center" : ""}` });
    // The brand's words in its own language where the page's has none of its own; an older
    // answer's heading in a language that isn't known.
    if (where === "above" && heading)
      box.append(
        h(
          "h2",
          { lang: t ? (t.langs?.heading ?? null) : "", style: own?.heading ?? null },
          heading,
        ),
      );
    if (t && t.place === where) {
      if (t.intro)
        box.append(
          h(
            "p",
            { class: "intro", lang: t.langs?.intro ?? null, style: own?.text ?? null },
            t.intro,
          ),
        );
      if (t.tagUs) {
        const line = tagLine(t.tagUs, w);
        if (own?.text) line.setAttribute("style", own.text);
        box.append(line);
      }
      if (t.button && safeUrl(t.button.url))
        box.append(
          h(
            "a",
            {
              class: "cta",
              href: safeUrl(t.button.url),
              target: "_blank",
              rel: "noopener",
              lang: t.langs?.button ?? null,
            },
            t.button.label,
          ),
        );
    }
    return box.childNodes.length ? box : null;
  };
  const above = words("above");
  if (above) section.append(above);
  if (heading) section.setAttribute("aria-label", heading);
  // The wall's labels as buttons for visitors (D-100).
  if (pick && (wall.labels?.length ?? 0) > 1) {
    const bar = h("div", { class: "labels", role: "group", "aria-label": w.showLabel });
    const choices: [string | null, string][] = [
      [null, w.allLabels],
      ...wall.labels!.map((l): [string, string] => [l.slug, l.name]),
    ];
    for (const [slug, name] of choices) {
      const b = h(
        "button",
        { type: "button", "aria-pressed": String((wall.label ?? null) === slug) },
        name,
      );
      b.addEventListener("click", () => pick(slug));
      bar.append(b);
    }
    section.append(bar);
  }
  if (stacked) {
    const list = h("div", { class: wall.layout, role: "list" }, ...tiles);
    section.append(list);
    // Big posts need rows as tall as a normal tile, so the wall measures its own width.
    if (grid && shown.some((p) => size(p) > 1) && "ResizeObserver" in window) {
      const fit = () => {
        const cols = getComputedStyle(list).gridTemplateColumns.split(" ").length || d.columns;
        const cell = (list.clientWidth - (cols - 1) * d.spacing) / cols;
        if (cell > 0) {
          list.style.gridAutoRows = `${cell * 1.25}px`;
          list.classList.add("sized");
        }
      };
      new ResizeObserver(fit).observe(list);
      fit();
    }
    if (infinite && "IntersectionObserver" in window) {
      const end = h("div", { class: "end", "aria-hidden": "true" });
      section.append(end);
      let busy = false;
      const more = async () => {
        if (busy) return;
        busy = true;
        try {
          if (shown.length >= all.length && next !== null && loadMore) {
            const page = await loadMore(next).catch(() => null);
            next = page?.next ?? null;
            if (page) all.push(...page.posts.filter((p) => !all.some((a) => a.id === p.id)));
          }
          const add = all.slice(shown.length, shown.length + batch);
          for (const p of add) {
            shown.push(p);
            list.append(makeTile(p, shown.length - 1));
          }
          if (shown.length >= all.length && next === null) io.disconnect();
          else {
            // Observing again reports at once if the end is still in view, so the page fills up.
            io.unobserve(end);
            io.observe(end);
          }
        } finally {
          busy = false;
        }
      };
      const io = new IntersectionObserver(
        (entries) => {
          if (entries.some((e) => e.isIntersecting)) void more();
        },
        { rootMargin: "600px 0px" },
      );
      io.observe(end);
    }
  } else {
    const row = h(
      "div",
      { class: "row", role: "list", tabindex: "0", "aria-label": w.scrollSideways },
      ...tiles,
    );
    section.append(row);
    if (wall.layout === "carousel") {
      const by = (dir: number) => () =>
        row.scrollBy({ left: dir * row.clientWidth * 0.9, behavior: "smooth" });
      const prev = h("button", { class: "arrow", type: "button", "aria-label": w.scrollBack }, "‹");
      const next = h(
        "button",
        { class: "arrow", type: "button", "aria-label": w.scrollForward },
        "›",
      );
      prev.addEventListener("click", by(-1));
      next.addEventListener("click", by(1));
      section.append(h("div", { class: "nav" }, prev, next));
    }
  }
  const below = words("below");
  if (below) section.append(below);
  if (wall.credit) {
    section.append(
      h(
        "a",
        {
          class: "credit",
          href: "https://brandwall.app/?ref=wall",
          target: "_blank",
          rel: "noopener",
        },
        w.poweredBy,
      ),
    );
  }
  root.replaceChildren(style, section, ...Array.from(root.querySelectorAll("dialog")));
}

/** The product on this page, for product-page walls: data-product, or Shopify's /products/handle. */
function pageProduct(el: HTMLElement): string | null {
  const given = el.dataset.product?.trim();
  if (given) return given;
  const m = location.pathname.match(/\/products\/([\w-]+)/);
  return m ? m[1]! : null;
}

/**
 * The page's language (D-097): data-lang on the wall, else the page's own lang, as its first
 * part ("da-DK" is "da") so pages in the same language share one cached wall.
 */
function pageLanguage(el: HTMLElement): string | null {
  const tag = el.dataset.lang || el.closest("[lang]")?.getAttribute("lang") || "";
  const primary = tag.trim().toLowerCase().split(/[-_]/)[0] ?? "";
  return /^[a-z]{2,3}$/.test(primary) ? primary : null;
}

/** Anonymous counts for Insights: no cookies, nothing about the shopper. */
function tracker(base: string, wallId: string): Track {
  const seen = new Set<string>();
  return (type, postId, product) => {
    const key = `${type}:${postId ?? ""}:${product?.id ?? ""}`;
    if (type !== "add" && seen.has(key)) return;
    seen.add(key);
    try {
      const body = JSON.stringify({ w: wallId, t: type, p: postId, k: product?.id || undefined });
      if (!navigator.sendBeacon?.(`${base}/v1/e`, new Blob([body], { type: "text/plain" }))) {
        fetch(`${base}/v1/e`, { method: "POST", body, keepalive: true, credentials: "omit" }).catch(
          () => {},
        );
      }
    } catch {
      // Counting must never get in the way.
    }
  };
}

export async function mount(el: HTMLElement, base: string) {
  const id = el.dataset.brandwall ?? "";
  if (!/^w_[a-z0-9]{12}$/.test(id)) return;
  const root = el.shadowRoot ?? el.attachShadow({ mode: "open" });
  try {
    const product = pageProduct(el);
    const lang = pageLanguage(el);
    // A page can show one label of the wall (D-100): data-label="women".
    // Brandwall reads it the way it makes labels' slugs, so "Store København" works too.
    const fixed = (el.dataset.label ?? "").trim().slice(0, 100) || null;
    let label = fixed;
    let picked = false;
    let taps = 0;
    const params = (offset?: number) => {
      const q = new URLSearchParams();
      if (offset) q.set("offset", String(offset));
      if (product) q.set("product", product);
      if (label) q.set("label", label);
      // A visitor's button never hides the wall for having few posts.
      if (picked) q.set("pick", "1");
      if (lang) q.set("lang", lang);
      const s = q.toString();
      return s ? `?${s}` : "";
    };
    const res = await fetch(`${base}/v1/walls/${id}.json${params()}`, { credentials: "omit" });
    if (!res.ok) return; // An unpublished or removed wall shows nothing, and never breaks the page.
    const wall = (await res.json()) as PublicWall;
    if (wall.posts.length === 0) return;
    const track = tracker(base, id);
    const loadMore = async (offset: number) => {
      const r = await fetch(`${base}/v1/walls/${id}.json${params(offset)}`, {
        credentials: "omit",
      });
      return r.ok ? ((await r.json()) as PublicWall) : null;
    };
    const report: Report = async (post, r) => {
      const res = await fetch(`${base}/v1/report`, {
        method: "POST",
        body: JSON.stringify({ wall: id, post, ...r }),
        headers: { "content-type": "text/plain" },
        credentials: "omit",
      });
      return res.ok;
    };
    const font = getComputedStyle(el).fontFamily;
    const site = wall.text?.style === "site" ? siteStyle(el, wall.design.background) : undefined;
    const show = (data: PublicWall) =>
      renderWall(
        root,
        data,
        font,
        track,
        loadMore,
        undefined,
        report,
        site,
        fixed
          ? undefined
          : async (next) => {
              label = next;
              picked = true;
              // Quick taps: only the last one's posts show.
              const n = ++taps;
              const r = await fetch(`${base}/v1/walls/${id}.json${params()}`, {
                credentials: "omit",
              }).catch(() => null);
              const data = r?.ok ? ((await r.json()) as PublicWall) : null;
              if (!data || n !== taps) return;
              show(data);
              // Keyboard users stay on the button they pressed.
              root.querySelector<HTMLElement>(".labels [aria-pressed=true]")?.focus();
            },
      );
    show(wall);
    track("view");
  } catch {
    // Never break the shop's page.
  }
}
